/* eslint-disable @typescript-eslint/naming-convention, jsdoc/require-param, jsdoc/require-returns */
import { extractNodeTransform } from "./nodeTransform";
import { extractUserProperties, getPropertyEntries, extractPropertyTemplates, getPropertyTemplate, resolveNumberProperty, resolvePropertyValue, resolveVector3Property, } from "./propertyTemplates";
import { findDocumentNode, findChildByName, getPropertyValue, cleanFBXName, getNodeArray } from "../types/fbxTypes";
import { resolveConnections, getChildren } from "./connections";
import { extractGeometry } from "./geometry";
import { extractLineGeometry, extractNurbsCurveGeometry, nurbsSurfaceToGeometry } from "./nurbs";
import { isLegacyDocument, upgradeLegacyDocument } from "./legacyDocument";
import { extractConstraints } from "./constraints";
import { extractMaterial, extractTextureRef } from "./materials";
import { extractSkins } from "./skeleton";
import { resolveRigs } from "./rig";
import { extractAnimations } from "./animation";
import { extractBlendShapes } from "./blendShapes";
import { extractSceneDiagnostics } from "./sceneDiagnostics";
/**
 * Tessellated NURBS vertices carry no control point mapping, so skin clusters and blend shapes bound to the surface
 * cannot be applied; they are reported and the surface is imported undeformed.
 */
function flagNurbsDeformers(objectMap, geometryId, geometry) {
    const kinds = getChildren(objectMap, geometryId, "Deformer")
        .map((d) => getPropertyValue(d.node, 2) ?? "")
        .filter((kind) => kind === "Skin" || kind === "BlendShape");
    if (kinds.length === 0) {
        return;
    }
    geometry.diagnostics.push({
        type: "nurbs-deformer-ignored",
        message: `${Array.from(new Set(kinds)).join(" and ")} deformer on NURBS surface '${geometry.name}' is ignored: the tessellated mesh has no control point mapping.`,
    });
}
export function interpretFBX(doc, options = {}) {
    if (isLegacyDocument(doc)) {
        doc = upgradeLegacyDocument(doc);
    }
    const objectMap = resolveConnections(doc);
    const propertyTemplates = extractPropertyTemplates(doc);
    // Extract global settings
    const globalSettings = extractGlobalSettings(doc);
    // Extract all materials
    const materials = [];
    for (const [id, node] of Array.from(objectMap.objects)) {
        if (node.name === "Material") {
            materials.push(extractMaterial(node, id, objectMap, propertyTemplates));
        }
    }
    attachLegacyLayerTextures(objectMap, materials, propertyTemplates);
    // Extract all geometries
    const geometries = [];
    const curves = [];
    for (const [id, node] of Array.from(objectMap.objects)) {
        if (node.name === "Geometry") {
            const subType = getPropertyValue(node, 2);
            if (subType === "Mesh") {
                const geometry = extractGeometry(node, id);
                // Edge-only or empty meshes have nothing to render; keep the node but drop the mesh.
                if (geometry.positions.length > 0 && geometry.indices.length > 0) {
                    geometries.push(geometry);
                }
            }
            else if (subType === "NurbsSurface") {
                const geometry = nurbsSurfaceToGeometry(node, id, options.nurbsSubdivision);
                if (geometry) {
                    flagNurbsDeformers(objectMap, id, geometry);
                    geometries.push(geometry);
                }
            }
            else if (subType === "TrimNurbsSurface") {
                // The trimmed surface is rendered untrimmed: the underlying NurbsSurface is a child of the trim.
                const surfaceChild = getChildren(objectMap, id, "Geometry").find((child) => getPropertyValue(child.node, 2) === "NurbsSurface");
                const geometry = surfaceChild ? nurbsSurfaceToGeometry(surfaceChild.node, id, options.nurbsSubdivision) : null;
                if (geometry) {
                    flagNurbsDeformers(objectMap, id, geometry);
                    geometry.diagnostics.push({
                        type: "nurbs-trim-ignored",
                        message: `Trim curves of NURBS surface '${geometry.name}' are ignored; the surface is rendered untrimmed.`,
                    });
                    geometries.push(geometry);
                }
            }
            else if (subType === "NurbsCurve") {
                curves.push(extractNurbsCurveGeometry(node, id));
            }
            else if (subType === "Line") {
                curves.push(extractLineGeometry(node, id));
            }
        }
    }
    // Extract skeleton/skinning data
    const skins = extractSkins(objectMap, propertyTemplates);
    const rigs = resolveRigs(objectMap, skins);
    // Extract blend shape data
    const blendShapes = extractBlendShapes(objectMap);
    // Extract animation data
    const animations = extractAnimations(objectMap, doc, { rebaseKeyframes: options.rebaseKeyframes ?? true });
    // Extract cameras and lights from NodeAttribute objects
    const cameras = extractCameras(objectMap, propertyTemplates);
    const lights = extractLights(objectMap, propertyTemplates);
    const diagnostics = extractSceneDiagnostics(objectMap);
    const constraints = extractConstraints(objectMap);
    // Build model hierarchy
    const rootModels = buildModelHierarchy(objectMap, geometries, curves, materials, propertyTemplates);
    const { displayLayers, selectionSets } = extractCollections(objectMap, rootModels);
    return {
        rootModels,
        displayLayers,
        selectionSets,
        constraints,
        geometries,
        curves,
        materials,
        skins,
        rigs,
        blendShapes,
        animations,
        cameras,
        lights,
        diagnostics,
        ...globalSettings,
    };
}
// ── Legacy textures ────────────────────────────────────────────────────────────
/**
 * Pre-7000 files connect textures to the Model and select them per polygon through the mesh's LayerElementTexture
 * (TextureId indexes the model's textures), instead of connecting textures to material properties. Assign those
 * textures to the diffuse slot of the material used by the same polygons.
 */
function attachLegacyLayerTextures(objectMap, materials, templates) {
    const materialById = new Map(materials.map((m) => [m.id, m]));
    const textureTemplate = getPropertyTemplate(templates, "Texture", "FbxFileTexture") ?? getPropertyTemplate(templates, "Texture");
    for (const [modelId, modelNode] of Array.from(objectMap.objects)) {
        if (modelNode.name !== "Model") {
            continue;
        }
        const textureChildren = getChildren(objectMap, modelId, "Texture");
        if (textureChildren.length === 0) {
            continue;
        }
        const modelMaterials = getChildren(objectMap, modelId, "Material")
            .filter((c) => !c.propertyName)
            .map((c) => materialById.get(c.id))
            .filter((m) => m !== undefined);
        if (modelMaterials.length === 0) {
            continue;
        }
        const geometryNode = getChildren(objectMap, modelId, "Geometry")[0]?.node ?? modelNode;
        const materialLayer = findChildByName(geometryNode, "LayerElementMaterial");
        const materialIds = materialLayer ? getNodeArray(findChildByName(materialLayer, "Materials")) : null;
        // Each texture kind has its own layer element: LayerElementTexture (diffuse), LayerElementEmissiveTextures,
        // LayerElementSpecularTextures, LayerElementBumpTextures, ...
        const slotForElement = (elementName) => {
            const kind = elementName.replace(/^LayerElement/, "").replace(/Textures?$/, "");
            switch (kind) {
                case "":
                case "Diffuse":
                    return { prop: "DiffuseColor", fbx: "diffuseColor", pbr: "baseColor" };
                case "Emissive":
                    return { prop: "EmissiveColor", fbx: "emissionColor", pbr: "emissionColor" };
                case "Specular":
                    return { prop: "SpecularColor", fbx: "specularColor", pbr: "specularColor" };
                case "Ambient":
                    return { prop: "AmbientColor", fbx: "ambientColor", pbr: "ambientOcclusion" };
                case "Bump":
                case "NormalMap":
                    return { prop: "NormalMap", fbx: "normalMap", pbr: "normalMap" };
                case "Transparent":
                    return { prop: "TransparentColor", fbx: "transparencyColor", pbr: "opacity" };
                case "Reflection":
                    return { prop: "ReflectionColor", fbx: "reflectionColor", pbr: "specularColor" };
                case "Shininess":
                    return { prop: "ShininessExponent", fbx: "specularExponent", pbr: "roughness" };
                case "Displacement":
                    return { prop: "DisplacementColor", fbx: "displacement", pbr: "displacementMap" };
                default:
                    return null;
            }
        };
        const layerElements = geometryNode.children.filter((c) => /^LayerElement\w*Textures?$/.test(c.name));
        const textureRefs = textureChildren.map(({ id, node }) => extractTextureRef(id, node, undefined, objectMap, textureTemplate));
        const assign = (materialIndex, textureIndex, slot) => {
            const material = modelMaterials[materialIndex];
            const source = textureRefs[textureIndex];
            if (!material || !source || material.textures.some((t) => t.propertyName === slot.prop)) {
                return;
            }
            const ref = { ...source, propertyName: slot.prop };
            material.textures.push(ref);
            const fbxMap = (material.model.fbx[slot.fbx] ??= { valueComponents: 0, textureEnabled: false });
            fbxMap.texture = ref;
            fbxMap.textureEnabled = true;
            const pbrMap = (material.model.pbr[slot.pbr] ??= { valueComponents: 0, textureEnabled: false });
            pbrMap.texture = ref;
            pbrMap.textureEnabled = true;
        };
        if (layerElements.length === 0) {
            // No layer element: a single texture on a single-material model is the diffuse map.
            if (modelMaterials.length === 1 && textureRefs.length === 1) {
                assign(0, 0, slotForElement("LayerElementTexture"));
            }
            continue;
        }
        for (const element of layerElements) {
            const slot = slotForElement(element.name);
            const textureIds = getNodeArray(findChildByName(element, "TextureId"));
            if (!slot || !textureIds || textureIds.length === 0) {
                continue;
            }
            const polygonCount = Math.max(textureIds.length, materialIds?.length ?? 0);
            for (let poly = 0; poly < polygonCount; poly++) {
                const texIndex = textureIds[Math.min(poly, textureIds.length - 1)];
                const matIndex = materialIds && materialIds.length > 0 ? materialIds[Math.min(poly, materialIds.length - 1)] : 0;
                if (texIndex >= 0) {
                    assign(matIndex, texIndex, slot);
                }
            }
        }
    }
}
// ── Model Hierarchy ────────────────────────────────────────────────────────────
const MAX_MODEL_DEPTH = 512;
function buildModelHierarchy(objectMap, geometries, curves, materials, propertyTemplates) {
    const geometryMap = new Map();
    for (const g of geometries) {
        geometryMap.set(g.id, g);
    }
    const curveMap = new Map();
    for (const c of curves) {
        curveMap.set(c.id, c);
    }
    const materialMap = new Map();
    for (const m of materials) {
        materialMap.set(m.id, m);
    }
    // Find root models (those connected to ID 0, which is the scene root)
    const rootChildren = objectMap.childrenOf.get(0) ?? [];
    const rootModels = [];
    const rootIds = new Set();
    const buildStack = new Set();
    for (const { id } of rootChildren) {
        const node = objectMap.objects.get(id);
        if (node && node.name === "Model" && !rootIds.has(id)) {
            rootIds.add(id);
            rootModels.push(buildModel(id, node, objectMap, geometryMap, curveMap, materialMap, propertyTemplates, buildStack));
        }
    }
    // Models without any Model (or scene root) parent connection also live at the root, as in the FBX SDK.
    // Typical examples are the default "Producer" cameras and the camera switcher written by Maya, MotionBuilder
    // and Blender in 6.x files, which are never connected.
    const hasModelParent = new Set();
    for (const conn of objectMap.connections) {
        if (conn.type !== "OO") {
            continue;
        }
        const parentNode = objectMap.objects.get(conn.parentId);
        if (conn.parentId === 0 || parentNode?.name === "Model") {
            hasModelParent.add(conn.childId);
        }
    }
    for (const [id, node] of Array.from(objectMap.objects)) {
        if (node.name === "Model" && !hasModelParent.has(id) && !rootIds.has(id)) {
            rootIds.add(id);
            rootModels.push(buildModel(id, node, objectMap, geometryMap, curveMap, materialMap, propertyTemplates, buildStack));
        }
    }
    return rootModels;
}
function buildModel(modelId, modelNode, objectMap, geometryMap, curveMap, materialMap, propertyTemplates, buildStack) {
    const name = cleanFBXName(getPropertyValue(modelNode, 1) ?? "Model");
    buildStack.add(modelId);
    const subType = getPropertyValue(modelNode, 2) ?? "Null";
    // Find attached geometry
    const geomChildren = getChildren(objectMap, modelId, "Geometry");
    const geometry = geomChildren.map((child) => geometryMap.get(child.id)).find((g) => g !== undefined);
    const curve = geomChildren.map((child) => curveMap.get(child.id)).find((c) => c !== undefined);
    // Find attached materials
    const matChildren = getChildren(objectMap, modelId, "Material");
    const modelMaterials = [];
    for (const { id } of matChildren) {
        const mat = materialMap.get(id);
        if (mat) {
            modelMaterials.push(mat);
        }
    }
    // Extract transform
    const transform = extractTransform(modelNode, getPropertyTemplate(propertyTemplates, "Model", "FbxNode") ?? getPropertyTemplate(propertyTemplates, "Model"));
    // Recursively build child models
    // Only object-object connections express parenting; OP connections (look-at targets, constraint sources) do not.
    const childModelNodes = getChildren(objectMap, modelId, "Model").filter((child) => !child.propertyName);
    const children = [];
    const cycleDiagnostics = [];
    for (const { id, node } of childModelNodes) {
        if (buildStack.size > MAX_MODEL_DEPTH) {
            cycleDiagnostics.push(`Model hierarchy deeper than ${MAX_MODEL_DEPTH} levels; deeper children were skipped.`);
            break;
        }
        if (buildStack.has(id)) {
            // Malformed files (duplicate ids, recursive connections) can make a model its own ancestor.
            cycleDiagnostics.push(`Model connection cycle detected at '${cleanFBXName(getPropertyValue(node, 1) ?? "")}'; the recursive child was skipped.`);
            continue;
        }
        children.push(buildModel(id, node, objectMap, geometryMap, curveMap, materialMap, propertyTemplates, buildStack));
    }
    buildStack.delete(modelId);
    // Extract culling
    const cullingNode = modelNode.children.find((c) => c.name === "Culling");
    const cullingOff = cullingNode ? getPropertyValue(cullingNode, 0) === "CullingOff" : false;
    // Extract user-defined custom properties
    const customProperties = extractCustomProperties(modelNode);
    // LOD groups carry their thresholds on the LodGroup node attribute
    let lodGroup;
    if (subType === "LodGroup") {
        const attribute = getChildren(objectMap, modelId, "NodeAttribute").find((a) => getPropertyValue(a.node, 2) === "LodGroup");
        const entries = attribute ? getPropertyEntries(attribute.node) : [];
        const thresholds = [];
        const displayLevels = [];
        for (let i = 0;; i++) {
            const entry = entries.find((e) => e.name === `Thresholds|Level${i}`);
            if (!entry) {
                break;
            }
            thresholds.push(typeof entry.values[0] === "number" ? entry.values[0] : 0);
        }
        for (let i = 0;; i++) {
            const entry = entries.find((e) => e.name === `DisplayLevels|Level${i}`);
            if (!entry) {
                break;
            }
            displayLevels.push(typeof entry.values[0] === "number" ? entry.values[0] : 0);
        }
        const relativeEntry = entries.find((e) => e.name === "ThresholdsUsedAsPercentage");
        lodGroup = { thresholds, relative: !!relativeEntry && relativeEntry.values[0] !== 0 && relativeEntry.values[0] !== false, displayLevels };
    }
    return {
        id: modelId,
        name,
        subType,
        geometry,
        materials: modelMaterials,
        children,
        cullingOff,
        customProperties,
        lodGroup,
        curve,
        ...transform,
        diagnostics: [...transform.diagnostics, ...cycleDiagnostics],
    };
}
/** Display layers and selection sets: which models belong to which collection. */
function extractCollections(objectMap, rootModels) {
    const modelById = new Map();
    const walk = (models) => {
        for (const m of models) {
            modelById.set(m.id, m);
            walk(m.children);
        }
    };
    walk(rootModels);
    const displayLayers = [];
    const selectionSets = [];
    for (const [id, node] of Array.from(objectMap.objects)) {
        if (node.name === "CollectionExclusive") {
            const entries = getPropertyEntries(node);
            const num = (name, fallback) => {
                const v = entries.find((e) => e.name === name)?.values[0];
                return typeof v === "number" ? v : typeof v === "boolean" ? (v ? 1 : 0) : fallback;
            };
            const colorValues = entries.find((e) => e.name === "Color")?.values;
            const color = colorValues && typeof colorValues[0] === "number" && typeof colorValues[1] === "number" && typeof colorValues[2] === "number"
                ? [colorValues[0], colorValues[1], colorValues[2]]
                : [0.8, 0.8, 0.8];
            const layer = {
                id,
                name: cleanFBXName(getPropertyValue(node, 1) ?? "DisplayLayer"),
                show: num("Show", 1) !== 0,
                freeze: num("Freeze", 0) !== 0,
                color,
                modelIds: getChildren(objectMap, id, "Model").map((c) => c.id),
            };
            displayLayers.push(layer);
            for (const modelId of layer.modelIds) {
                const model = modelById.get(modelId);
                if (model) {
                    model.displayLayer = layer;
                }
            }
        }
        else if (node.name === "SelectionSet" || (node.name === "Collection" && getPropertyValue(node, 2) === "SelectionSet")) {
            const members = [];
            for (const child of getChildren(objectMap, id)) {
                if (child.node.name === "Model") {
                    members.push({ modelId: child.id, includeNode: true });
                }
                else if (child.node.name === "SelectionNode") {
                    const indices = (name) => {
                        const arrayNode = findChildByName(child.node, name);
                        if (!arrayNode) {
                            return undefined;
                        }
                        return Array.from(getNodeArray(arrayNode));
                    };
                    const inSet = findChildByName(child.node, "IsTheNodeInSet")?.properties[0]?.value;
                    for (const target of getChildren(objectMap, child.id, "Model")) {
                        members.push({
                            modelId: target.id,
                            includeNode: inSet === undefined || inSet === 1 || inSet === true,
                            vertices: indices("VertexIndexArray"),
                            edges: indices("EdgeIndexArray"),
                            faces: indices("PolygonIndexArray"),
                        });
                    }
                }
            }
            selectionSets.push({ id, name: cleanFBXName(getPropertyValue(node, 1) ?? "SelectionSet"), members });
        }
    }
    return { displayLayers, selectionSets };
}
function extractTransform(modelNode, template) {
    return extractNodeTransform(modelNode, template);
}
// Frame rates per FBX TimeMode enum value (eDefaultMode, eFrames120, ... eFrames59dot94).
const TIME_MODE_FPS = [30, 120, 100, 60, 50, 48, 30, 30, 29.97, 29.97, 25, 24, 1000, 23.976, 24, 96, 72, 59.94];
function extractGlobalSettings(doc) {
    const defaults = {
        upAxis: 1,
        upAxisSign: 1,
        frontAxis: 2,
        frontAxisSign: 1,
        coordAxis: 0,
        coordAxisSign: 1,
        unitScaleFactor: 1,
        frameRate: 30,
    };
    const gsNode = findDocumentNode(doc, "GlobalSettings");
    if (!gsNode) {
        return defaults;
    }
    let timeMode = 0;
    let customFrameRate = 24;
    for (const { name: propName, values } of getPropertyEntries(gsNode)) {
        const value = toNumber(values[0]);
        if (propName && value !== undefined) {
            switch (propName) {
                case "UpAxis":
                    defaults.upAxis = value;
                    break;
                case "UpAxisSign":
                    defaults.upAxisSign = value;
                    break;
                case "FrontAxis":
                    defaults.frontAxis = value;
                    break;
                case "FrontAxisSign":
                    defaults.frontAxisSign = value;
                    break;
                case "CoordAxis":
                    defaults.coordAxis = value;
                    break;
                case "CoordAxisSign":
                    defaults.coordAxisSign = value;
                    break;
                case "UnitScaleFactor":
                    defaults.unitScaleFactor = value;
                    break;
                case "TimeMode":
                    timeMode = value;
                    break;
                case "CustomFrameRate":
                    customFrameRate = value;
                    break;
            }
        }
    }
    if (timeMode === 14) {
        defaults.frameRate = customFrameRate > 0 ? customFrameRate : 24;
    }
    else {
        defaults.frameRate = TIME_MODE_FPS[timeMode] ?? 30;
    }
    return defaults;
}
// ── Cameras & Lights ──────────────────────────────────────────────────────────
const SYSTEM_PROPERTIES = new Set([
    "Lcl Translation",
    "Lcl Rotation",
    "Lcl Scaling",
    "PreRotation",
    "PostRotation",
    "RotationPivot",
    "ScalingPivot",
    "RotationOffset",
    "ScalingOffset",
    "RotationOrder",
    "GeometricTranslation",
    "GeometricRotation",
    "GeometricScaling",
    "Visibility",
    "InheritType",
    "ScalingMax",
    "DefaultAttributeIndex",
    "currentUVSet",
    "lockInfluenceWeights",
]);
function extractCustomProperties(modelNode) {
    // Properties flagged "U" are user-defined. Some writers (MotionBuilder) flag their own template properties
    // as user properties too, so the well-known node properties are filtered out again.
    const flagged = extractUserProperties(modelNode);
    if (!flagged) {
        return undefined;
    }
    let hasAny = false;
    const custom = {};
    for (const name of Object.keys(flagged)) {
        if (!SYSTEM_PROPERTIES.has(name)) {
            custom[name] = flagged[name];
            hasAny = true;
        }
    }
    return hasAny ? custom : undefined;
}
const CAMERA_PROPERTIES = new Set([
    "FieldOfView",
    "FieldOfViewX",
    "FieldOfViewY",
    "NearPlane",
    "FarPlane",
    "AspectWidth",
    "AspectHeight",
    "FilmAspectRatio",
    "FocalLength",
    "FilmWidth",
    "FilmHeight",
    "ApertureWidth",
    "ApertureHeight",
    "CameraProjectionType",
    "ProjectionType",
    "OrthoZoom",
    "Roll",
    "ApertureMode",
]);
const LIGHT_PROPERTIES = new Set([
    "LightType",
    "Color",
    "Intensity",
    "InnerAngle",
    "OuterAngle",
    "ConeAngle",
    "DecayType",
    "DecayStart",
    "EnableNearAttenuation",
    "EnableFarAttenuation",
    "CastShadow",
    "Shadow",
]);
function extractCameras(objectMap, templates) {
    const cameras = [];
    const cameraTemplate = getPropertyTemplate(templates, "NodeAttribute", "FbxCamera") ?? getPropertyTemplate(templates, "NodeAttribute");
    for (const [id, node] of Array.from(objectMap.objects)) {
        if (node.name !== "NodeAttribute") {
            continue;
        }
        const subType = getPropertyValue(node, 2);
        if (subType !== "Camera") {
            continue;
        }
        // Find the model this camera is attached to (parent)
        const parent = objectMap.parentOf.get(id);
        if (!parent) {
            continue;
        }
        const parentNode = objectMap.objects.get(parent.id);
        if (!parentNode || parentNode.name !== "Model") {
            continue;
        }
        const name = cleanFBXName(getPropertyValue(parentNode, 1) ?? "Camera");
        const nearPlane = resolveNumberProperty(node, cameraTemplate, "NearPlane", 0.1);
        const farPlane = resolveNumberProperty(node, cameraTemplate, "FarPlane", 10000);
        const projectionType = resolveNumberProperty(node, cameraTemplate, "CameraProjectionType", 0) === 1 || resolveNumberProperty(node, cameraTemplate, "ProjectionType", 0) === 1
            ? "orthographic"
            : "perspective";
        const focalLength = toNumber(resolvePropertyValue(node, cameraTemplate, "FocalLength"));
        const filmWidth = toNumber(resolvePropertyValue(node, cameraTemplate, "FilmWidth")) ?? toNumber(resolvePropertyValue(node, cameraTemplate, "ApertureWidth"));
        const filmHeight = toNumber(resolvePropertyValue(node, cameraTemplate, "FilmHeight")) ?? toNumber(resolvePropertyValue(node, cameraTemplate, "ApertureHeight"));
        const orthoZoom = toNumber(resolvePropertyValue(node, cameraTemplate, "OrthoZoom"));
        const roll = toNumber(resolvePropertyValue(node, cameraTemplate, "Roll"));
        const resolved = resolveCameraParameters(node, cameraTemplate);
        const aspectRatio = resolved.aspectRatio;
        const fieldOfView = resolved.fovDegY;
        const diagnostics = [];
        if (projectionType === "orthographic" && orthoZoom === undefined) {
            diagnostics.push("Orthographic camera has no OrthoZoom; runtime orthographic bounds use a fallback.");
        }
        if (focalLength !== undefined && filmHeight === undefined && resolvePropertyValue(node, cameraTemplate, "FieldOfView") === undefined) {
            diagnostics.push("FocalLength is present without FilmHeight; default field of view fallback may be used.");
        }
        cameras.push({
            modelId: parent.id,
            attributeId: id,
            userProperties: extractUserProperties(node),
            name,
            fieldOfView,
            fieldOfViewX: resolved.fovDegX,
            apertureMode: resolved.apertureMode,
            apertureSizeInch: resolved.apertureSizeInch,
            orthographicSize: resolved.orthographicSize,
            nearPlane,
            farPlane,
            aspectRatio,
            projectionType,
            focalLength,
            filmWidth,
            filmHeight,
            orthoZoom,
            roll,
            unknownProperties: collectUnknownLocalProperties(node, CAMERA_PROPERTIES),
            diagnostics,
        });
    }
    return cameras;
}
function extractLights(objectMap, templates) {
    const lights = [];
    const lightTemplate = getPropertyTemplate(templates, "NodeAttribute", "FbxLight") ?? getPropertyTemplate(templates, "NodeAttribute");
    for (const [id, node] of Array.from(objectMap.objects)) {
        if (node.name !== "NodeAttribute") {
            continue;
        }
        const subType = getPropertyValue(node, 2);
        if (subType !== "Light") {
            continue;
        }
        // Find the model this light is attached to
        const parent = objectMap.parentOf.get(id);
        if (!parent) {
            continue;
        }
        const parentNode = objectMap.objects.get(parent.id);
        if (!parentNode || parentNode.name !== "Model") {
            continue;
        }
        const name = cleanFBXName(getPropertyValue(parentNode, 1) ?? "Light");
        const lightType = resolveNumberProperty(node, lightTemplate, "LightType", 0);
        const color = resolveVector3Property(node, lightTemplate, "Color", [1, 1, 1]);
        const intensity = resolveNumberProperty(node, lightTemplate, "Intensity", 100) / 100;
        const outerAngle = toNumber(resolvePropertyValue(node, lightTemplate, "OuterAngle")) ?? toNumber(resolvePropertyValue(node, lightTemplate, "ConeAngle"));
        const innerAngle = toNumber(resolvePropertyValue(node, lightTemplate, "InnerAngle"));
        const coneAngle = outerAngle ?? 45;
        const decayType = resolveNumberProperty(node, lightTemplate, "DecayType", 2);
        const decayStart = toNumber(resolvePropertyValue(node, lightTemplate, "DecayStart"));
        const enableNearAttenuation = toBoolean(resolvePropertyValue(node, lightTemplate, "EnableNearAttenuation"));
        const enableFarAttenuation = toBoolean(resolvePropertyValue(node, lightTemplate, "EnableFarAttenuation"));
        const castShadows = toBoolean(resolvePropertyValue(node, lightTemplate, "CastShadow")) ??
            toBoolean(resolvePropertyValue(parentNode, undefined, "CastShadow")) ??
            toBoolean(resolvePropertyValue(parentNode, undefined, "Shadow"));
        const diagnostics = [];
        if (decayType !== 2) {
            diagnostics.push(`DecayType ${decayType} is preserved as metadata; Babylon falloff is not remapped in this pass.`);
        }
        if (decayStart !== undefined) {
            diagnostics.push("DecayStart is preserved as metadata and is not mapped to Babylon light range.");
        }
        lights.push({
            modelId: parent.id,
            attributeId: id,
            userProperties: extractUserProperties(node),
            nearAttenuationStart: toNumber(resolvePropertyValue(node, lightTemplate, "NearAttenuationStart")),
            nearAttenuationEnd: toNumber(resolvePropertyValue(node, lightTemplate, "NearAttenuationEnd")),
            farAttenuationStart: toNumber(resolvePropertyValue(node, lightTemplate, "FarAttenuationStart")),
            farAttenuationEnd: toNumber(resolvePropertyValue(node, lightTemplate, "FarAttenuationEnd")),
            areaLightShape: toNumber(resolvePropertyValue(node, lightTemplate, "AreaLightShape")),
            name,
            lightType,
            color,
            intensity,
            coneAngle,
            decayType,
            innerAngle,
            outerAngle,
            decayStart,
            enableNearAttenuation,
            enableFarAttenuation,
            castShadows,
            unknownProperties: collectUnknownLocalProperties(node, LIGHT_PROPERTIES),
            diagnostics,
        });
    }
    return lights;
}
// ── Utilities ──────────────────────────────────────────────────────────────────
function toNumber(value) {
    if (typeof value === "number") {
        return value;
    }
    return undefined;
}
function toBoolean(value) {
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "number") {
        return value !== 0;
    }
    return undefined;
}
/** Film back sizes in thousandths of an inch per FBX ApertureFormat enum value. */
const APERTURE_FORMATS = [
    [1000, 1000],
    [404, 295],
    [493, 292],
    [864, 630],
    [816, 612],
    [980, 735],
    [825, 446],
    [864, 732],
    [2066, 906],
    [1485, 991],
    [2080, 1480],
    [2772, 2072],
];
/**
 * Resolves the camera's projection parameters the way the FBX SDK does: aspect ratio from AspectWidth/Height and
 * the aspect mode, aperture (film gate) from the film size, squeeze ratio and gate fit, and the horizontal and
 * vertical field of view from the aperture mode (horizontal, vertical, both, or focal length).
 */
function resolveCameraParameters(node, template) {
    const num = (name, fallback) => toNumber(resolvePropertyValue(node, template, name)) ?? fallback;
    const aspectMode = num("AspectRatioMode", 0);
    const apertureMode = num("ApertureMode", 2);
    const apertureFormat = Math.max(0, Math.min(APERTURE_FORMATS.length - 1, Math.round(num("ApertureFormat", 0))));
    const gateFit = num("GateFit", 0);
    let aspectX = num("AspectW", 0);
    let aspectY = num("AspectH", 0);
    aspectX = num("AspectWidth", aspectX);
    aspectY = num("AspectHeight", aspectY);
    const fov = num("FieldOfView", 0);
    const fovX = num("FieldOfViewX", 0);
    const fovY = num("FieldOfViewY", 0);
    const focalLength = num("FocalLength", 0);
    const orthoExtent = 30 * num("OrthoZoom", 1);
    const format = APERTURE_FORMATS[apertureFormat];
    let filmX = num("FilmWidth", format[0] * 0.001);
    let filmY = num("FilmHeight", format[1] * 0.001);
    const squeeze = num("FilmSqueezeRatio", apertureFormat === 7 ? 2 : 1);
    if (aspectX <= 0 && aspectY <= 0) {
        aspectX = filmX > 0 ? filmX : 1;
        aspectY = filmY > 0 ? filmY : 1;
    }
    else if (aspectX <= 0) {
        aspectX = filmX > 0 && filmY > 0 ? (aspectY / filmY) * filmX : aspectY;
    }
    else if (aspectY <= 0) {
        aspectY = filmX > 0 && filmY > 0 ? (aspectX / filmX) * filmY : aspectX;
    }
    filmY *= squeeze;
    let resX = aspectX;
    let resY = aspectY;
    switch (aspectMode) {
        case 3: // fixed width
            resX = aspectX;
            resY = aspectX * aspectY;
            break;
        case 4: // fixed height
            resX = aspectY * aspectX;
            resY = aspectY;
            break;
        default:
            break;
    }
    const aspectRatio = resY !== 0 ? resX / resY : 1;
    const filmRatio = filmY !== 0 ? filmX / filmY : 1;
    let effectiveFit = gateFit;
    if (effectiveFit === 3) {
        effectiveFit = aspectRatio > filmRatio ? 2 : 1;
    }
    else if (effectiveFit === 4) {
        effectiveFit = aspectRatio < filmRatio ? 2 : 1;
    }
    let apertureX = filmX;
    let apertureY = filmY;
    let orthoX = orthoExtent;
    let orthoY = orthoExtent;
    switch (effectiveFit) {
        case 1: // vertical
            apertureX = filmY * aspectRatio;
            apertureY = filmY;
            orthoX = orthoExtent * aspectRatio;
            break;
        case 2: // horizontal
            apertureX = filmX;
            apertureY = filmX / aspectRatio;
            orthoY = orthoExtent / aspectRatio;
            break;
        default:
            break;
    }
    filmX = apertureX;
    void filmX;
    const d2r = Math.PI / 180;
    const r2d = 180 / Math.PI;
    let tanX;
    let tanY;
    switch (apertureMode) {
        case 0:
            tanX = Math.tan(fovX * d2r * 0.5);
            tanY = Math.tan(fovY * d2r * 0.5);
            break;
        case 1:
            tanX = Math.tan(fov * d2r * 0.5);
            tanY = tanX / aspectRatio;
            break;
        case 3: {
            const focalInch = focalLength / 25.4;
            tanX = focalInch > 0 ? (apertureX / focalInch) * 0.5 : Math.tan(fov * d2r * 0.5) * aspectRatio;
            tanY = focalInch > 0 ? (apertureY / focalInch) * 0.5 : Math.tan(fov * d2r * 0.5);
            break;
        }
        default:
            tanY = Math.tan(fov * d2r * 0.5);
            tanX = tanY * aspectRatio;
            break;
    }
    return {
        fovDegX: Math.atan(tanX) * r2d * 2,
        fovDegY: Math.atan(tanY) * r2d * 2,
        aspectRatio,
        apertureMode,
        apertureSizeInch: [apertureX, apertureY],
        orthographicSize: [orthoX, orthoY],
    };
}
function collectUnknownLocalProperties(node, known) {
    const unknown = new Set();
    for (const containerName of ["Properties70", "Properties60"]) {
        const container = findChildByName(node, containerName);
        for (const propertyNode of container?.children ?? []) {
            if (propertyNode.name !== "P" && propertyNode.name !== "Property") {
                continue;
            }
            const propertyName = getPropertyValue(propertyNode, 0);
            if (propertyName && !known.has(propertyName)) {
                unknown.add(propertyName);
            }
        }
    }
    return Array.from(unknown).sort();
}
//# sourceMappingURL=fbxInterpreter.js.map